import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassCircleIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../components/UI/PageHeader';
import { Card, CardContent } from '../components/UI/Card';
import { Button } from '../components/UI/Button';
import { Input } from '../components/UI/Input';
import { Textarea } from '../components/UI/Textarea';
import { Label } from '../components/UI/Label';
import { Alert, AlertDescription } from '../components/UI/Alert';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { formatDate } from '../utils/i18nFormat';
import {
  TakedownRequest,
  listTakedowns,
  createTakedown,
  resolveContact,
  updateNotice,
  submitForApproval,
  approveAndSend,
  rejectTakedown,
  markResolved,
} from '../services/takedowns';

const STATUS_BADGE: Record<string, string> = {
  draft: 'badge-secondary',
  contact_resolved: 'badge-primary',
  pending_approval: 'badge-warning',
  approved: 'badge-warning',
  sent: 'badge-primary',
  acknowledged: 'badge-primary',
  resolved: 'badge-success',
  rejected: 'badge-danger',
  failed: 'badge-danger',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  contact_resolved: 'Contato resolvido',
  pending_approval: 'Aguardando aprovação',
  approved: 'Aprovado',
  sent: 'Enviado',
  acknowledged: 'Reconhecido',
  resolved: 'Resolvido',
  rejected: 'Rejeitado',
  failed: 'Falhou',
};

const Takedowns: React.FC = () => {
  const { user } = useAuth();
  const isClient = (user as any)?.role === 'CLIENT';
  const [items, setItems] = useState<TakedownRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<TakedownRequest | null>(null);
  const [creating, setCreating] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editContact, setEditContact] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await listTakedowns();
      setItems(data);
    } catch {
      toast.error('Erro ao carregar takedowns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (selected) {
      setEditSubject(selected.notice_subject || '');
      setEditBody(selected.notice_body || '');
      setEditContact(selected.abuse_contact_email || '');
    }
  }, [selected]);

  const refreshSelected = (updated: TakedownRequest) => {
    setSelected(updated);
    setItems(prev => prev.map(t => (t.id === updated.id ? updated : t)));
  };

  const handleCreate = async () => {
    if (!newUrl.trim()) return;
    setBusy(true);
    try {
      const created = await createTakedown({ target_url: newUrl.trim() });
      setItems(prev => [created, ...prev]);
      setNewUrl('');
      setCreating(false);
      setSelected(created);
      toast.success('Takedown criado — notificação gerada automaticamente');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao criar takedown');
    } finally {
      setBusy(false);
    }
  };

  const withBusy = async (fn: () => Promise<TakedownRequest>, successMsg?: string) => {
    setBusy(true);
    try {
      const updated = await fn();
      refreshSelected(updated);
      if (successMsg) toast.success(successMsg);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ação falhou');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdits = async () => {
    if (!selected) return;
    await withBusy(
      () => updateNotice(selected.id, { notice_subject: editSubject, notice_body: editBody, abuse_contact_email: editContact || undefined }),
      'Notificação atualizada',
    );
  };

  return (
    <div>
      <PageHeader title='Automação de Takedown' subtitle='Notificações de abuso geradas e enviadas com aprovação humana em cada disparo'>
        {!isClient && (
          <Button onClick={() => setCreating(v => !v)}>
            <PlusIcon className='h-4 w-4 mr-2' />
            Novo takedown
          </Button>
        )}
      </PageHeader>

      {creating && (
        <Card className='mb-6'>
          <CardContent className='flex items-end gap-3'>
            <div className='flex-1 space-y-1.5'>
              <Label htmlFor='new-url'>URL do conteúdo malicioso</Label>
              <Input
                id='new-url'
                placeholder='https://dominio-falso.com/login'
                value={newUrl}
                onChange={e => setNewUrl(e.target.value)}
              />
            </div>
            <Button onClick={handleCreate} disabled={busy || !newUrl.trim()}>
              {busy ? <LoadingSpinner size='sm' className='mr-2' /> : null}
              Gerar notificação
            </Button>
          </CardContent>
        </Card>
      )}

      <div className='grid grid-cols-1 lg:grid-cols-5 gap-6'>
        {/* List */}
        <div className='lg:col-span-2'>
          <Card>
            <CardContent className='p-0'>
              {loading ? (
                <div className='flex justify-center py-12'><LoadingSpinner size='lg' /></div>
              ) : items.length === 0 ? (
                <p className='text-center text-sm text-gray-500 dark:text-gray-400 py-12'>Nenhum takedown ainda.</p>
              ) : (
                <div className='divide-y divide-gray-100 dark:divide-gray-800'>
                  {items.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setSelected(t)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${selected?.id === t.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}
                    >
                      <div className='flex justify-between items-center'>
                        <p className='text-sm font-medium text-gray-900 dark:text-white truncate'>{t.target_domain || t.target_url}</p>
                        <span className={`badge ${STATUS_BADGE[t.status]}`}>{STATUS_LABEL[t.status]}</span>
                      </div>
                      <p className='text-xs text-gray-500 dark:text-gray-400 mt-0.5'>
                        {t.created_at ? formatDate(t.created_at, { year: 'numeric', month: '2-digit', day: '2-digit' }) : ''}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail */}
        <div className='lg:col-span-3'>
          {!selected ? (
            <Card>
              <CardContent className='text-center py-16 text-sm text-gray-500 dark:text-gray-400'>
                Selecione um takedown na lista para ver detalhes.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className='space-y-4'>
                <div className='flex justify-between items-start'>
                  <div>
                    <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>{selected.target_domain}</h3>
                    <a href={selected.target_url} target='_blank' rel='noopener noreferrer' className='text-xs text-primary-600 hover:underline break-all'>
                      {selected.target_url}
                    </a>
                  </div>
                  <span className={`badge ${STATUS_BADGE[selected.status]}`}>{STATUS_LABEL[selected.status]}</span>
                </div>

                {selected.last_error && (
                  <Alert variant='destructive'>
                    <div className='flex items-start gap-2'>
                      <ExclamationTriangleIcon className='h-4 w-4 mt-0.5 shrink-0' />
                      <AlertDescription>{selected.last_error}</AlertDescription>
                    </div>
                  </Alert>
                )}

                <div className='space-y-1.5'>
                  <Label htmlFor='contact-email'>Contato de abuse</Label>
                  <div className='flex gap-2'>
                    <Input
                      id='contact-email'
                      placeholder='ainda não resolvido'
                      value={editContact}
                      onChange={e => setEditContact(e.target.value)}
                      disabled={isClient || ['sent', 'acknowledged', 'resolved'].includes(selected.status)}
                    />
                    {!isClient && !['sent', 'acknowledged', 'resolved'].includes(selected.status) && (
                      <Button
                        variant='outline'
                        onClick={() => withBusy(() => resolveContact(selected.id))}
                        disabled={busy}
                        title='Resolver automaticamente via RDAP'
                      >
                        <MagnifyingGlassCircleIcon className='h-4 w-4' />
                      </Button>
                    )}
                  </div>
                  {selected.abuse_contact_source && (
                    <p className='text-xs text-gray-500 dark:text-gray-400'>Fonte: {selected.abuse_contact_source}</p>
                  )}
                </div>

                <div className='space-y-1.5'>
                  <Label htmlFor='notice-subject'>Assunto</Label>
                  <Input
                    id='notice-subject'
                    value={editSubject}
                    onChange={e => setEditSubject(e.target.value)}
                    disabled={isClient || ['sent', 'acknowledged', 'resolved'].includes(selected.status)}
                  />
                </div>

                <div className='space-y-1.5'>
                  <Label htmlFor='notice-body'>Notificação (gerada automaticamente, editável)</Label>
                  <Textarea
                    id='notice-body'
                    rows={10}
                    value={editBody}
                    onChange={e => setEditBody(e.target.value)}
                    disabled={isClient || ['sent', 'acknowledged', 'resolved'].includes(selected.status)}
                    className='font-mono text-xs'
                  />
                </div>

                {!isClient && (
                  <div className='flex flex-wrap justify-end gap-2 pt-2'>
                    {!['sent', 'acknowledged', 'resolved', 'rejected'].includes(selected.status) && (
                      <Button variant='outline' onClick={handleSaveEdits} disabled={busy}>
                        Salvar edições
                      </Button>
                    )}

                    {['draft', 'contact_resolved', 'failed'].includes(selected.status) && (
                      <Button
                        variant='outline'
                        onClick={() => withBusy(() => submitForApproval(selected.id), 'Enviado para aprovação')}
                        disabled={busy || !editContact}
                      >
                        Enviar para aprovação
                      </Button>
                    )}

                    {selected.status === 'pending_approval' && (
                      <>
                        <Button
                          variant='destructive'
                          onClick={() => withBusy(() => rejectTakedown(selected.id), 'Takedown rejeitado')}
                          disabled={busy}
                        >
                          <XCircleIcon className='h-4 w-4 mr-2' />
                          Rejeitar
                        </Button>
                        <Button
                          onClick={() => withBusy(() => approveAndSend(selected.id), 'Notificação enviada')}
                          disabled={busy}
                        >
                          {busy ? <LoadingSpinner size='sm' className='mr-2' /> : <PaperAirplaneIcon className='h-4 w-4 mr-2' />}
                          Aprovar e enviar
                        </Button>
                      </>
                    )}

                    {['sent', 'acknowledged'].includes(selected.status) && (
                      <Button onClick={() => withBusy(() => markResolved(selected.id), 'Marcado como resolvido')} disabled={busy}>
                        <CheckCircleIcon className='h-4 w-4 mr-2' />
                        Marcar como resolvido
                      </Button>
                    )}

                    {selected.status === 'failed' && (
                      <Button variant='outline' onClick={() => withBusy(() => resolveContact(selected.id))} disabled={busy}>
                        <ArrowPathIcon className='h-4 w-4 mr-2' />
                        Tentar novamente
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Takedowns;
